/*
 * Copyright (c) 2022 OTB Africa. All rights reserved. <https://otbafrica.com>
 *
 * Created Date: Tuesday, February 22nd 2022, 2:31:28 pm
 * Author: OTB Africa, developers@otbafrica.com
 *
 * Use of this source code is governed by the license that can be found in
 * the LICENSE file or at https://developers.otbafrica.com/licenses/
 */

import React from "react";
import { ServiceBuilderContext, IServiceBuilderContext } from "./ServiceBuilderContext";
import { useProcessDefinitionsAPI } from "service-builder/services";
import { useAPIGetUserTaskTypes } from "service-builder/services";
import { useFormBuilderStore, currentFormSelector } from "form-builder/stores";
import { useParams } from "react-router";
import { useDeepEffect, useDeepCallback } from "lib/deep_hooks";
import {
  ServiceBuilderAction,
  setCurrentProcessDefinition,
  setFetchProcessDefinitionStatus,
  setPatchProcessDefinitionStatus,
} from "./actionCreators";
import { SERVICE_BUILDER_ACTIONS } from "./actionTypes";
import { FormConfiguration, ProcessDefinition } from "lib/schemas";

type ServiceBuilderReducerState = Omit<
  IServiceBuilderContext,
  | "dispatchServiceBuilderAction"
  | "updateTemplateArtifactsOnAPISaveFormConfiguration"
  | "patchCurrentProcessDefinition"
  | "removeFormFromProcessDefinition"
>;

type ServiceBuilderReducer = (
  previousState: ServiceBuilderReducerState,
  action: ServiceBuilderAction
) => ServiceBuilderReducerState;

const serviceBuilderReducer: ServiceBuilderReducer = (previousState, action) => {
  switch (action.type) {
    case SERVICE_BUILDER_ACTIONS.SET_FETCH_PROCESS_DEFINITION_STATUS:
      return {
        ...previousState,
        processDefinitionContext: {
          ...previousState.processDefinitionContext,
          isFetchingProcessDefinition: action.payload.isFetchingProcessDefinition,
          processDefinitionFetchHasError: action.payload.processDefinitionFetchHasError,
          fetchProcessDefinitionStatusCode: action.payload.fetchProcessDefinitionStatusCode,
        },
      };
    case SERVICE_BUILDER_ACTIONS.SET_CURRENT_PROCESS_DEFINITION:
      return {
        ...previousState,
        processDefinitionContext: {
          ...previousState.processDefinitionContext,
          currentProcessDefinition: action.payload.processDefinition,
        },
      };
    case SERVICE_BUILDER_ACTIONS.SET_NEW_STATUS_FOR_CURRENT_PROCESS_DEFINITION:
      return {
        ...previousState,
        processDefinitionContext: {
          ...previousState.processDefinitionContext,
          newStatusForCurrentProcessDefinition: action.payload.newStatusForCurrentProcessDefinition,
        },
      };
    case SERVICE_BUILDER_ACTIONS.SET_PATCH_PROCESS_DEFINITION_STATUS:
      return {
        ...previousState,
        processDefinitionContext: {
          ...previousState.processDefinitionContext,
          processDefinitionPatchState: action.payload,
        },
      };
    default:
      return previousState;
  }
};

interface ServiceBuilderProviderProps {
  children: React.ReactNode;
}

const initialServiceBuilderState: ServiceBuilderReducerState = {
  formBuilderContext: { currentFormConfiguration: null },
  processDefinitionContext: {
    currentProcessDefinition: null,
    newStatusForCurrentProcessDefinition: null,
    isFetchingProcessDefinition: false,
    processDefinitionFetchHasError: false,
    fetchProcessDefinitionStatusCode: 0,
    processDefinitionPatchState: {
      isPatchingProcessDefinition: false,
      isSuccessfulProcessDefinitionPatch: false,
      isProcessDefinitionPatchError: false,
      processDefinitionPatchStatusCode: 0,
    },
  },
};

export const ServiceBuilderProvider = ({ children }: ServiceBuilderProviderProps) => {
  const { serviceId } = useParams();

  const { useAPIFetchProcessDefinition, useAPIPatchProcessDefinition } = useProcessDefinitionsAPI();

  const { loadUserTaskTypesFromAPI } = useAPIGetUserTaskTypes();

  const currentFormConfiguration = useFormBuilderStore(currentFormSelector);

  const [serviceBuilderState, dispatchServiceBuilderAction] = React.useReducer<ServiceBuilderReducer>(
    serviceBuilderReducer,
    initialServiceBuilderState
  );

  const {
    fetchItem,
    apiItem: fetchedProcessDefinitionResponse,
    isMakingAPIRequest: isFetchingProcessDefinition,
    isAPIError: processDefinitionFetchHasError,
    httpResponseStatusCode: fetchProcessDefinitionStatusCode,
  } = useAPIFetchProcessDefinition();

  React.useEffect(() => loadUserTaskTypesFromAPI(), [loadUserTaskTypesFromAPI]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  React.useEffect(() => fetchItem(serviceId), [serviceId]);

  React.useEffect(
    () =>
      dispatchServiceBuilderAction(
        setFetchProcessDefinitionStatus(
          isFetchingProcessDefinition,
          processDefinitionFetchHasError,
          fetchProcessDefinitionStatusCode
        )
      ),
    [isFetchingProcessDefinition, processDefinitionFetchHasError, fetchProcessDefinitionStatusCode]
  );

  useDeepEffect(() => {
    fetchedProcessDefinitionResponse?.id &&
      dispatchServiceBuilderAction(setCurrentProcessDefinition(fetchedProcessDefinitionResponse));
  }, [fetchedProcessDefinitionResponse]);

  const currentProcessDefinition = serviceBuilderState?.processDefinitionContext?.currentProcessDefinition;

  const {
    patchProcessDefinition,
    isMakingAPIRequest: isPatchingProcessDefinition,
    isSuccessfulAPIRequest: isSuccessfulProcessDefinitionPatch,
    isAPIError: isProcessDefinitionPatchError,
    httpResponseStatusCode: processDefinitionPatchStatusCode,
  } = useAPIPatchProcessDefinition((patchedProcessDefinitionResponse: ProcessDefinition) =>
    dispatchServiceBuilderAction(setCurrentProcessDefinition(patchedProcessDefinitionResponse))
  );

  // Function that updates various process template artifacts when a form is saved since different artifacts, mainly
  // Process Definition and Task Template have foreign keys to the forms model.
  // TODO: consider putting this in its own hook since alot will be done with this.
  const updateTemplateArtifactsOnAPISaveFormConfiguration = useDeepCallback(
    (
      updatedFormconfiguration: FormConfiguration,
      update_as: FormConfiguration["form_type"],
      onSave?: (p: ProcessDefinition) => void
    ) => {
      let patchedProcessDefinitionData = null;

      switch (update_as) {
        case "registration_form":
          if (currentProcessDefinition.service_registration_forms.includes(updatedFormconfiguration.id)) return;

          patchedProcessDefinitionData = {
            service_registration_forms: [
              ...currentProcessDefinition.service_registration_forms,
              updatedFormconfiguration.id,
            ],
          };
          break;
        case "application_form":
          if (currentProcessDefinition.submission_forms.includes(updatedFormconfiguration.id)) return;

          patchedProcessDefinitionData = {
            submission_forms: [...currentProcessDefinition.submission_forms, updatedFormconfiguration.id],
          };
          break;
      }

      if (patchedProcessDefinitionData)
        patchProcessDefinition(currentProcessDefinition?.id, patchedProcessDefinitionData, onSave);
    },
    [patchProcessDefinition, currentProcessDefinition] // Using a deep callback here so that whenever the any data in the currentProcessDefinition changes, we patch with he most recent data.
  );

  const removeFormFromProcessDefinition = useDeepCallback(
    (form: FormConfiguration, remove_as: FormConfiguration["form_type"]) => {
      let patchedProcessDefinitionData = null;

      switch (remove_as) {
        case "registration_form":
          if (!currentProcessDefinition.service_registration_forms.includes(form.id)) return;

          patchedProcessDefinitionData = {
            service_registration_forms: [
              ...currentProcessDefinition.service_registration_forms.filter((formId) => formId !== form.id),
            ],
          };
          break;
        case "application_form":
          if (!currentProcessDefinition.submission_forms.includes(form.id)) return;

          patchedProcessDefinitionData = {
            submission_forms: [...currentProcessDefinition.submission_forms.filter((formId) => formId !== form.id)],
          };
          break;
      }

      if (patchedProcessDefinitionData)
        patchProcessDefinition(currentProcessDefinition?.id, patchedProcessDefinitionData);
    },
    [patchProcessDefinition, currentProcessDefinition] // Using a deep callback here so that whenever the any data in the currentProcessDefinition changes, we patch with he most recent data.
  );

  const patchCurrentProcessDefinition = React.useCallback(
    (patchedData: Partial<Omit<ProcessDefinition, "id">>) => {
      if (currentProcessDefinition?.id) patchProcessDefinition(currentProcessDefinition.id, patchedData);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [currentProcessDefinition?.id]
  );

  React.useEffect(() => {
    dispatchServiceBuilderAction(
      setPatchProcessDefinitionStatus({
        isPatchingProcessDefinition,
        isSuccessfulProcessDefinitionPatch,
        isProcessDefinitionPatchError,
        processDefinitionPatchStatusCode,
      })
    );
  }, [
    isPatchingProcessDefinition,
    isSuccessfulProcessDefinitionPatch,
    isProcessDefinitionPatchError,
    processDefinitionPatchStatusCode,
  ]);

  return (
    <ServiceBuilderContext.Provider
      value={{
        ...serviceBuilderState,
        formBuilderContext: { currentFormConfiguration },
        dispatchServiceBuilderAction,
        updateTemplateArtifactsOnAPISaveFormConfiguration,
        removeFormFromProcessDefinition,
        patchCurrentProcessDefinition,
      }}
    >
      {children}
    </ServiceBuilderContext.Provider>
  );
};
